import qrcode from 'qrcode-generator';
import {
  ACCOUNT_ADDRESS,
  PATH_STEPS,
  PATH_TITLES,
  ccdBalance,
  choosePath,
  enoughCcd,
  findLei,
  formatCcd,
  ONBOARDING_CCD_MICRO,
  openQuestions,
  pathProgress,
  type Answer,
  type Answers,
  type PathId,
  type StepId,
} from '../../../packages/core/src/start-path.ts';
import type { Fetch } from '../../../packages/core/src/chain.ts';
import { isValidLei, normaliseLei } from '../../../packages/core/src/gleif.ts';
import { companyFinder } from './company-finder.ts';

/**
 * /start/ and /start/<id>: the guided onboarding (README "Onboarding paths").
 * Two (or three) questions pick a path A–E; each path is a short checklist
 * with one action per step. Progress is a server-side draft keyed by the
 * random id in the address (plus localStorage as a convenience). External
 * sites open in a new tab; PageID ticks steps itself where it can see them:
 * the wallet extension appearing, the LEI at GLEIF, the account's company
 * identity, the CCD balance, and (from the draft) onboarding, domains and the
 * binding record.
 */
type Progress = { customer_id: string; customer_url: string; verified: boolean; domains: number; domains_verified: number; bound: boolean } | null;
type Draft = {
  id: string;
  answers: { lei: Answer | null; wallet: Answer | null; companyId: Answer | null };
  path: PathId | null;
  done: StepId[];
  lei: string | null;
  company_name: string | null;
  company_country: string | null;
  account: string | null;
  expires_at: string;
  progress: Progress;
};

const LINKS = {
  extension: 'https://chromewebstore.google.com/detail/concordium-wallet/mnnkpffndmickbiakofclnpoiajlegmg',
  appStore: 'https://apps.apple.com/app/concordium-wallet/id1593386457',
  googlePlay: 'https://play.google.com/store/apps/details?id=com.pioneeringtechventures.wallet',
  companyIdGuide: 'https://docs.concordium.com/en/mainnet/tutorials/company-identity/company-identities.html',
  globalFinReg: 'https://globalfinreg.com/',
  globalFinRegContact: 'https://globalfinreg.com/en/contact',
  gleifIssuers: 'https://www.gleif.org/en/about-lei/get-an-lei-find-lei-issuing-organizations',
  getCcd: 'https://www.concordium.com/ccd-token',
};

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const UA = navigator.userAgent;
const IOS = /iPhone|iPad|iPod/i.test(UA) || (/Macintosh/.test(UA) && navigator.maxTouchPoints > 1);
const ANDROID = /Android/i.test(UA);
const MOBILE = IOS || ANDROID || /Mobile/i.test(UA);
const STORE_KEY = 'pageid-start';
const netFetch: Fetch = (url, init) => fetch(url, init as RequestInit) as never;

let draft: Draft = { id: '', answers: { lei: null, wallet: null, companyId: null }, path: null, done: [], lei: null, company_name: null, company_country: null, account: null, expires_at: '', progress: null };
let walletFound = false;
let openStep: StepId | null = null;
let balance: { micro: number; at: number } | null = null;
let timers: number[] = [];

function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, string> = {}, ...kids: (Node | string | null | false)[]) {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, v);
  for (const k of kids) if (k) n.append(k);
  return n;
}
const ext = (href: string, text: string, cls = 'btn primary block') => h('a', { class: cls, href, target: '_blank', rel: 'noopener noreferrer' }, text);

function toast(text: string) {
  const t = h('div', { class: 'toast' }, text);
  $('toasts').append(t);
  setTimeout(() => t.remove(), 3500);
}

const local = {
  get(): string | null {
    try {
      return localStorage.getItem(STORE_KEY);
    } catch {
      return null;
    }
  },
  set(id: string | null) {
    try {
      if (id) localStorage.setItem(STORE_KEY, id);
      else localStorage.removeItem(STORE_KEY);
    } catch {
      /* private mode: the link still works */
    }
  },
};

// ---- the draft on the server ----------------------------------------------------------
async function api<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, body === undefined ? {} : { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  const j = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw Object.assign(new Error(j.error ?? `HTTP ${res.status}`), { status: res.status });
  return j;
}

let saving: Promise<void> = Promise.resolve();
/**
 * Saves a change (creating the draft on the first one) and keeps the address
 * bar on /start/<id>. The page's own state is the newest; from the server it
 * takes only the id, the expiry and PageID's progress.
 */
function save(patch: Partial<Omit<Draft, 'answers'>> & { answers?: Partial<Draft['answers']> }): Promise<void> {
  const { answers, ...rest } = patch;
  if (answers) draft.answers = { ...draft.answers, ...answers };
  Object.assign(draft, rest);
  const body = { ...rest, ...(answers ? { answers: { ...draft.answers }, path: draft.path } : {}) };
  const snapshot = { answers: { ...draft.answers }, path: draft.path, done: [...draft.done], lei: draft.lei, company_name: draft.company_name, company_country: draft.company_country, account: draft.account };
  saving = saving.then(async () => {
    try {
      const fresh = !draft.id;
      const d = fresh ? await api<Draft>('/api/start', snapshot) : await api<Draft>(`/api/start/${draft.id}`, body);
      draft = { ...draft, id: d.id, expires_at: d.expires_at, progress: d.progress };
      if (fresh) {
        history.replaceState(null, '', `/start/${d.id}${location.hash}`);
        local.set(d.id);
      }
    } catch (e) {
      toast(`Not saved: ${(e as Error).message}`);
    }
  });
  return saving;
}

// ---- the browser wallet -----------------------------------------------------------------
type Provider = { requestAccounts(): Promise<string[]>; getMostRecentlySelectedAccount(): Promise<string | undefined> };
async function detectWallet(ms = 1500): Promise<Provider | null> {
  if (MOBILE) return null;
  try {
    const { detectConcordiumProvider } = await import('@concordium/browser-wallet-api-helpers');
    return (await detectConcordiumProvider(ms)) as unknown as Provider;
  } catch {
    return null;
  }
}

type AccountCheck = { account: string; idp: number; company: boolean; lei: boolean };
/** Connects the browser wallet (free, no transaction) and asks PageID's node whether the account holds a company identity. */
async function checkWallet(): Promise<{ result: AccountCheck | null; account: string | null; note: string }> {
  const p = await detectWallet(2500);
  if (!p) return { result: null, account: null, note: 'No Concordium Wallet found in this browser.' };
  const accounts = await p.requestAccounts();
  const account = (await p.getMostRecentlySelectedAccount().catch(() => undefined)) ?? accounts[0];
  if (!account) return { result: null, account: null, note: 'The wallet connected no account.' };
  try {
    return { result: await api<AccountCheck>(`/api/start/account/${account}`), account, note: '' };
  } catch (e) {
    const status = (e as { status?: number }).status;
    if (status === 404) return { result: null, account, note: 'This account is not on Concordium mainnet yet.' };
    return { result: null, account, note: "PageID couldn't check the account right now. Go ahead: the connect step's proof will tell, and a wallet without a company identity is sent to “Get a company identity”." };
  }
}

// ---- 1. the questions -----------------------------------------------------------------------
function renderQuestions() {
  for (const b of document.querySelectorAll<HTMLButtonElement>('.choice')) {
    const q = b.dataset.q as keyof Answers;
    b.setAttribute('aria-pressed', String(draft.answers[q] === b.dataset.v));
  }
  $('q-cid').hidden = draft.answers.wallet !== 'yes';
  const showLookup = draft.answers.lei === 'unsure' || (draft.answers.lei === 'yes' && !draft.lei);
  $('lookup').hidden = !showLookup;
  if (showLookup) {
    $('lookup-label').textContent = draft.answers.lei === 'yes' ? 'Add it now (optional): find your company, or type the LEI.' : 'Let’s look it up: tell us where your company is registered and its name.';
    questionFinder ??= leiFinder('q', draft.answers.lei === 'yes' ? 'lei' : 'find', onQuestionPick);
    if (!$('lookup-box').contains(questionFinder.el)) $('lookup-box').replaceChildren(questionFinder.el);
    if (lastLeiAnswer !== draft.answers.lei) questionFinder.setMode(draft.answers.lei === 'yes' ? 'lei' : 'find');
  }
  lastLeiAnswer = draft.answers.lei;
  const lf = $('lei-found');
  lf.hidden = !draft.lei;
  if (draft.lei) lf.replaceChildren(h('span', { class: 'tick' }), h('span', {}, h('b', {}, draft.company_name ?? 'Your company'), ` · LEI ${draft.lei}`), h('button', { class: 'btn ghost sm', type: 'button', id: 'lei-clear' }, 'Change'));
  $('lei-clear')?.addEventListener('click', () => void save({ lei: null, company_name: null }).then(renderQuestions));
  const wf = $('wallet-found');
  wf.hidden = !walletFound;
  if (walletFound) wf.replaceChildren(h('span', { class: 'tick' }), 'Concordium Wallet found in this browser');
  $('q-wallet-hint').textContent = MOBILE ? 'The Concordium Wallet app on this phone.' : 'The browser extension (Concordium Wallet for Web) or the Concordium Wallet app.';
  const path = choosePath(draft.answers);
  $('result').hidden = !path;
  if (path) {
    const n = PATH_STEPS[path].length;
    $('result-title').textContent = `${path} · ${PATH_TITLES[path]}`;
    $('result-line').textContent = `${n} steps${path === 'A' ? ', about 10 minutes' : ''}. We save your progress as you go.`;
  }
}

async function answer(q: keyof Answers, v: Answer) {
  const answers: Partial<Draft['answers']> = { [q]: v };
  if (q === 'wallet' && v !== 'yes') answers.companyId = null;
  await save({ answers, path: choosePath({ ...draft.answers, ...answers }) });
  renderQuestions();
  if (q === 'lei' && v !== 'no') questionFinder?.focus();
  if (q === 'wallet' && v === 'unsure') {
    const p = await detectWallet(2500);
    walletFound = !!p;
    if (p) {
      toast('Found the Concordium Wallet in this browser');
      await save({ answers: { wallet: 'yes' }, path: choosePath({ ...draft.answers, wallet: 'yes' }) });
    } else toast(MOBILE ? "No problem: we'll show you where to get the app." : "Not found in this browser: we'll help you install it.");
    renderQuestions();
  }
  if (q === 'companyId' && v === 'unsure') renderCidCheck();
  else if (q === 'companyId') $('cid-check').hidden = true;
  if (!openQuestions(draft.answers).length) $('result').scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

function renderCidCheck() {
  const box = $('cid-check');
  box.hidden = false;
  if (!walletFound) {
    box.replaceChildren(h('p', { class: 'muted' }, MOBILE ? 'Open the Concordium Wallet app → Identities. A company identity shows as Global FinReg (Business ID).' : 'Open the Concordium Wallet → Identities. A company identity shows as Global FinReg (Business ID).'), h('p', { class: 'faint' }, "Still not sure? Go ahead: the connect step's proof tells, and if there is none we take you to “Get a company identity”."));
    return;
  }
  const btn = h('button', { class: 'btn primary block', type: 'button' }, 'Check with my browser wallet');
  const out = h('p', { class: 'muted', 'aria-live': 'polite' }, 'Free: the wallet only shares your account address.');
  btn.addEventListener('click', async () => {
    btn.setAttribute('disabled', '');
    out.textContent = 'Asking the wallet…';
    try {
      const r = await checkWallet();
      if (r.account) await save({ account: r.account });
      if (r.result?.company) {
        out.textContent = '✓ This account has a company identity from Global FinReg.';
        await save({ answers: { companyId: 'yes' }, path: choosePath({ ...draft.answers, companyId: 'yes' }), done: [...new Set([...draft.done, 'wallet', 'identity', 'approval', 'account'] as StepId[])] });
      } else if (r.result) {
        out.textContent = 'This account has a personal identity. PageID needs a company identity from Global FinReg (Business ID).';
        await save({ answers: { companyId: 'no' }, path: choosePath({ ...draft.answers, companyId: 'no' }) });
      } else out.textContent = r.note;
      renderQuestions();
    } catch (e) {
      out.textContent = `The wallet said: ${(e as Error).message}`;
    } finally {
      btn.removeAttribute('disabled');
    }
  });
  box.replaceChildren(btn, out);
}

type Picked = { lei: string; legalName: string; country: string | null };

/**
 * Finding the company's LEI: "Find my company" (country, name, optional
 * registration number → PageID's company lookup, README "Company lookup") or
 * "I know my LEI" (typed, checked at GLEIF). `onPick(null)` = "We don't have an LEI".
 */
function leiFinder(prefix: string, mode: 'find' | 'lei', onPick: (m: Picked | null) => void | Promise<void>) {
  const findBtn = h('button', { type: 'button', id: `${prefix}-mode-find`, 'aria-pressed': 'false', 'aria-controls': `${prefix}-panel-find` }, 'Find my company');
  const leiBtn = h('button', { type: 'button', id: `${prefix}-mode-lei`, 'aria-pressed': 'false', 'aria-controls': `${prefix}-panel-lei` }, 'I know my LEI');
  const seg = h('div', { class: 'seg lookup-modes', role: 'group', 'aria-label': 'How to find your LEI' }, findBtn, leiBtn);
  const finder = companyFinder({
    id: `${prefix}-f`,
    country: draft.company_country,
    onPick: (c) => onPick({ lei: c.lei, legalName: c.legalName, country: c.country }),
    onNoLei: () => onPick(null),
    onCountry: (cc) => {
      draft.company_country = cc;
    },
  });
  const findPanel = h('div', { class: 'lookup-panel', id: `${prefix}-panel-find` }, finder);
  const form = h('form', { class: 'mini-lookup', novalidate: '', id: `${prefix}-lei-form` }) as HTMLFormElement;
  const input = h('input', { type: 'text', id: `${prefix}-lei`, class: 'mono', maxlength: '24', autocomplete: 'off', spellcheck: 'false', placeholder: '20 letters and digits' }) as HTMLInputElement;
  const go = h('button', { class: 'btn primary', type: 'submit', id: `${prefix}-lei-btn` }, 'Look up');
  form.append(h('label', { for: `${prefix}-lei` }, 'Your LEI'), h('div', { class: 'inline' }, input, go), h('p', { class: 'hint' }, 'We check it live at GLEIF, the global LEI index.'));
  const out = h('div', { 'aria-live': 'polite', id: `${prefix}-lei-result` });
  const leiPanel = h('div', { class: 'lookup-panel', id: `${prefix}-panel-lei` }, form, out);
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const lei = normaliseLei(input.value);
    if (!isValidLei(lei)) return void out.replaceChildren(h('p', { class: 'error', role: 'alert' }, 'That is not an LEI (20 letters and digits). Don’t know it? Use “Find my company”.'));
    go.setAttribute('disabled', '');
    out.replaceChildren(h('p', { class: 'faint' }, 'Looking it up at GLEIF…'));
    try {
      const [m] = await findLei(lei, netFetch);
      if (!m) return void out.replaceChildren(h('div', { class: 'match none' }, h('p', { class: 'match-kind' }, 'Not found'), h('p', { class: 'muted' }, `GLEIF has no record for ${lei}. Check it, or use “Find my company”.`)));
      const yes = h('button', { class: 'btn primary', type: 'button' }, 'Yes, that’s us');
      yes.addEventListener('click', () => void onPick({ lei: m.lei, legalName: m.legalName, country: m.country }));
      const no = h('button', { class: 'btn', type: 'button' }, 'No, not us');
      no.addEventListener('click', () => {
        out.replaceChildren();
        input.focus();
      });
      const meta = [m.country, m.city, m.registeredAs && `registered as ${m.registeredAs}`, m.status?.toLowerCase()].filter(Boolean).join(' · ');
      out.replaceChildren(h('div', { class: 'match' }, h('p', { class: 'match-kind' }, 'Found'), h('p', { class: 'match-name' }, m.legalName), h('p', { class: 'match-meta mono' }, `LEI ${m.lei}`), meta ? h('p', { class: 'faint' }, meta) : null, h('div', { class: 'row' }, yes, no)));
    } catch (err) {
      out.replaceChildren(h('p', { class: 'error' }, (err as Error).message));
    } finally {
      go.removeAttribute('disabled');
    }
  });
  let current = mode;
  const setMode = (m: 'find' | 'lei') => {
    current = m;
    findBtn.setAttribute('aria-pressed', String(m === 'find'));
    leiBtn.setAttribute('aria-pressed', String(m === 'lei'));
    findPanel.hidden = m !== 'find';
    leiPanel.hidden = m !== 'lei';
  };
  findBtn.addEventListener('click', () => setMode('find'));
  leiBtn.addEventListener('click', () => {
    setMode('lei');
    input.focus();
  });
  setMode(mode);
  const el = h('div', { class: 'lei-finder' }, seg, findPanel, leiPanel);
  return {
    el,
    setMode,
    focus: () => (current === 'lei' ? input : (el.querySelector<HTMLInputElement>(`#${prefix}-f-${draft.company_country ? 'name' : 'country'}`) ?? input)).focus(),
  };
}

let questionFinder: ReturnType<typeof leiFinder> | null = null;
let lastLeiAnswer: Answer | null = null;
async function onQuestionPick(m: Picked | null) {
  if (m) await save({ lei: m.lei, company_name: m.legalName, company_country: m.country ?? draft.company_country, answers: { lei: 'yes' }, path: choosePath({ ...draft.answers, lei: 'yes' }) });
  else await save({ lei: null, answers: { lei: 'no' }, path: choosePath({ ...draft.answers, lei: 'no' }) });
  questionFinder = null;
  renderQuestions();
  toast(m ? `Saved: ${m.legalName}` : 'No LEI yet: we show you how to get one.');
}

for (const b of document.querySelectorAll<HTMLButtonElement>('.choice')) b.addEventListener('click', () => void answer(b.dataset.q as keyof Answers, b.dataset.v as Answer));
$('result-go').addEventListener('click', () => {
  if (!draft.path) return;
  showPath();
  scrollTo(0, 0);
});
$('change').addEventListener('click', () => {
  showQuestions();
  scrollTo(0, 0);
});

// ---- 2. the path ------------------------------------------------------------------------------
type StepDef = { title: string; render(body: HTMLElement): void; doneLabel: string; time?: string };

const lines = (...t: string[]) => t.map((s) => h('p', { class: 'muted' }, s));
const comeBack = () => h('p', { class: 'faint back-note' }, 'Opens in a new tab. Come back here when you are done: this page keeps your place.');

function storeButtons() {
  if (IOS) return [ext(LINKS.appStore, 'Get the app (App Store)')];
  if (ANDROID) return [ext(LINKS.googlePlay, 'Get the app (Google Play)')];
  return [ext(LINKS.extension, 'Get the browser extension')];
}

function walletCheckButton(label: string, onCompany: () => void) {
  if (MOBILE || !walletFound) return null;
  const out = h('p', { class: 'faint', 'aria-live': 'polite' });
  const b = h('button', { class: 'btn', type: 'button' }, label);
  b.addEventListener('click', async () => {
    b.setAttribute('disabled', '');
    out.textContent = 'Asking the wallet…';
    try {
      const r = await checkWallet();
      if (r.account) await save({ account: r.account });
      if (r.result?.company) {
        out.textContent = '✓ Company identity found.';
        onCompany();
      } else out.textContent = r.result ? 'Not yet: this account has a personal identity, not a company identity.' : r.note;
    } catch (e) {
      out.textContent = `The wallet said: ${(e as Error).message}`;
    } finally {
      b.removeAttribute('disabled');
    }
  });
  return h('div', { class: 'stack' }, b, out);
}
const markIdentity = () => void markDone(['identity', 'approval', 'account']);

const STEPS: Record<StepId, StepDef> = {
  wallet: {
    title: 'Install the Concordium Wallet',
    doneLabel: "It's installed",
    render(body) {
      body.append(
        ...lines(MOBILE ? 'Install the Concordium Wallet app and set it up.' : 'Add the Concordium Wallet for Web to this browser (Chrome, Edge or Brave). PageID sees it as soon as it is installed.'),
        ...storeButtons(),
        h('p', { class: 'faint' }, MOBILE ? 'On a computer? Use the ' : 'Prefer your phone? The app: ', MOBILE ? h('a', { href: LINKS.extension, target: '_blank', rel: 'noopener noreferrer' }, 'browser extension') : h('span', {}, h('a', { href: LINKS.appStore, target: '_blank', rel: 'noopener noreferrer' }, 'App Store'), ' · ', h('a', { href: LINKS.googlePlay, target: '_blank', rel: 'noopener noreferrer' }, 'Google Play')), '.'),
        comeBack(),
      );
      if (!MOBILE) {
        const re = h('button', { class: 'btn ghost sm', type: 'button' }, 'Installed it? Check again');
        re.addEventListener('click', () => location.reload());
        body.append(h('p', { class: 'faint watch' }, h('span', { class: 'pulse' }), 'Watching for the extension…'), re);
      }
    },
  },
  lei: {
    title: 'Get an LEI',
    doneLabel: 'We have our LEI',
    render(body) {
      body.append(
        ...lines('PageID needs your company’s LEI (Legal Entity Identifier). Two ways to get one:'),
        h(
          'ul',
          { class: 'ways' },
          h('li', {}, h('b', {}, 'With Global FinReg. '), 'They issue the company identity (next steps) and are also an LEI registration agent. Ask them whether they can get your LEI as part of the Business ID application: ', h('a', { href: LINKS.globalFinRegContact, target: '_blank', rel: 'noopener noreferrer' }, 'contact Global FinReg'), '.'),
          h('li', {}, h('b', {}, 'From any GLEIF-accredited issuer. '), 'GLEIF lists them by country.'),
        ),
        ext(LINKS.gleifIssuers, 'Find an LEI issuer (GLEIF)'),
        comeBack(),
        h('p', { class: 'faint' }, 'Prices and processing times depend on the issuer; PageID does not know them.'),
      );
      const msg = h('p', { class: 'faint', 'aria-live': 'polite' });
      const f = leiFinder('step', 'find', async (m) => {
        if (!m) return void (msg.textContent = 'Once GLEIF lists your LEI (it can take a little while after it is issued), look it up here.');
        await save({ lei: m.lei, company_name: m.legalName, company_country: m.country ?? draft.company_country });
        await markDone(['lei']);
      });
      body.append(h('p', { class: 'card-title ready-title' }, 'Got it? Find it at GLEIF'), f.el, msg);
    },
  },
  identity: {
    title: 'Get a company identity at Global FinReg',
    doneLabel: "I've sent the application",
    render(body) {
      body.append(
        ...lines('In the Concordium Wallet: Identities → + → Global FinReg (Business ID). Sign up at Global FinReg and follow their steps.'),
        h('p', { class: 'card-title ready-title' }, 'Have ready'),
        h(
          'ul',
          { class: 'ready' },
          h('li', {}, 'Your LEI', draft.lei ? h('span', { class: 'mono' }, ` ${draft.lei}`) : ''),
          h('li', {}, 'A recent extract from your company register, no older than 3 months'),
          h('li', {}, 'The passport or driving licence of the owner (UBO) or an authorised signatory'),
        ),
        h('p', { class: 'note info' }, 'A personal identity will not work: PageID needs a company identity from Global FinReg.'),
        ext(LINKS.companyIdGuide, "Open Concordium's step-by-step guide"),
        comeBack(),
      );
      const c = walletCheckButton('Already approved? Check my wallet', markIdentity);
      if (c) body.append(c);
    },
  },
  approval: {
    title: "Wait for Global FinReg's approval",
    doneLabel: "It's approved",
    time: 'Global FinReg does not publish how long it takes.',
    render(body) {
      body.append(...lines('Global FinReg checks your documents and the signatory, then issues the identity into your wallet.', 'You can close this page now. Bookmark the link below and come back when the identity shows in your wallet.'));
      const c = walletCheckButton('Check my wallet', markIdentity);
      if (c) body.append(c);
    },
  },
  account: {
    title: 'Create a company account',
    doneLabel: 'Account created',
    time: 'Free, a minute in the wallet.',
    render(body) {
      body.append(...lines('In the wallet, create an account from your new company identity. PageID’s proof and payments come from this account.'));
      const c = walletCheckButton('Check my wallet', markIdentity);
      if (c) body.append(c);
    },
  },
  ccd: {
    title: `Add about ${formatCcd(ONBOARDING_CCD_MICRO)} to the company account`,
    doneLabel: 'The CCD is there',
    render(body) {
      body.append(
        ...lines('PageID’s onboarding costs about 11 CCD in all, a few cents: the proof request (≈ 4.5 CCD), the session transfer (≈ 1 CCD) and the binding record (≈ 5 CCD), plus small fees. Checking a site is always free for visitors.'),
      );
      const box = h('div', { class: 'ccd-box' });
      body.append(box);
      renderCcd(box);
      body.append(ext(LINKS.getCcd, 'Where to get CCD (Concordium)', 'btn block'), comeBack(), h('p', { class: 'faint' }, 'Need help with CCD? ', h('a', { href: '/contact/' }, 'Contact us'), '.'));
    },
  },
  connect: {
    title: 'Prove your company with PageID',
    doneLabel: 'Verified',
    time: 'About 10 minutes.',
    render(body) {
      body.append(...lines('One wallet session: your wallet proves your company identity (name, business number, country, LEI) and sends about 1 CCD from the company account to PageID. You then get your sign-in link.'));
      const q = new URLSearchParams({ ...(draft.lei ? { lei: draft.lei } : {}), start: draft.id });
      body.append(h('a', { class: 'btn primary block', href: `/onboard/?${q}` }, 'Start the connect step'));
      body.append(h('p', { class: 'faint' }, 'This step ticks itself when PageID has verified your company.'));
    },
  },
  domains: {
    title: 'Verify your domains',
    doneLabel: 'Domains verified',
    render(body) {
      const p = draft.progress;
      body.append(...lines('On your customer page, publish one DNS TXT record or one .well-known file per domain, then press Check.'));
      if (p?.domains) body.append(h('p', { class: 'faint' }, `${p.domains_verified} of ${p.domains} verified.`));
      body.append(h('a', { class: 'btn primary block', href: p?.customer_url ?? '/signin/' }, 'Open my customer page'));
    },
  },
  binding: {
    title: 'Register your binding record',
    doneLabel: 'Binding registered',
    render(body) {
      body.append(...lines('Send it from your company account with the browser wallet (≈ 5 CCD, once). The PageID label then shows your company as verified. Then add the label to your pages.'));
      body.append(h('a', { class: 'btn primary block', href: draft.progress?.customer_url ?? '/signin/' }, 'Open my customer page'));
    },
  },
};

function renderCcd(box: HTMLElement) {
  if (!draft.account) {
    const form = h('form', { class: 'mini-lookup', novalidate: '' }) as HTMLFormElement;
    const input = h('input', { type: 'text', id: 'ccd-account', class: 'mono', placeholder: 'e.g. 49FMtVKk81Qw…', spellcheck: 'false', autocomplete: 'off' }) as HTMLInputElement;
    const err = h('p', { class: 'error', role: 'alert' });
    form.append(h('label', { for: 'ccd-account' }, 'Your company account address'), h('div', { class: 'inline' }, input, h('button', { class: 'btn', type: 'submit' }, 'Use it')), err);
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const a = input.value.trim();
      if (!ACCOUNT_ADDRESS.test(a)) return void (err.textContent = 'That is not a Concordium account address (50 characters).');
      await save({ account: a });
      renderPath();
    });
    box.append(form);
    if (walletFound) {
      const b = h('button', { class: 'btn ghost sm', type: 'button' }, 'Take it from my browser wallet');
      b.addEventListener('click', async () => {
        const r = await checkWallet().catch((e) => ({ account: null, result: null, note: (e as Error).message }));
        if (r.account) {
          await save({ account: r.account });
          renderPath();
        } else err.textContent = r.note;
      });
      box.append(b);
    }
    return;
  }
  const qrc = h('canvas', { class: 'addr-qr', width: '0', height: '0', 'aria-label': 'QR code of the account address' }) as HTMLCanvasElement;
  drawQr(qrc, draft.account);
  const copy = h('button', { class: 'copy-btn', type: 'button' }, 'Copy');
  copy.addEventListener('click', () => copyText(copy, draft.account!));
  const change = h('button', { class: 'btn ghost sm', type: 'button' }, 'Another account');
  change.addEventListener('click', async () => {
    balance = null;
    await save({ account: null });
    renderPath();
  });
  const have = balance ? formatCcd(balance.micro) : '…';
  const ok = balance ? enoughCcd(balance.micro) : false;
  box.append(
    h('p', { class: 'card-title' }, 'Send CCD to this account'),
    h('div', { class: 'addr' }, h('div', { class: 'code-wrap' }, h('pre', { class: 'code', id: 'ccd-address' }, draft.account), copy), qrc),
    h('div', { class: `balance ${ok ? 'ok' : ''}`, id: 'ccd-balance' }, h('span', { class: 'meter' }, h('i', { style: `width:${balance ? Math.min(100, (balance.micro / ONBOARDING_CCD_MICRO) * 100).toFixed(1) : 0}%` })), h('span', {}, h('b', {}, have), ` of ${formatCcd(ONBOARDING_CCD_MICRO)}`, ok ? ' · enough' : ' · checking every 10 s')),
    change,
  );
}

function drawQr(c: HTMLCanvasElement, text: string) {
  const qr = qrcode(0, 'M');
  qr.addData(text, 'Byte');
  qr.make();
  const n = qr.getModuleCount();
  const s = 4;
  c.width = c.height = (n + 8) * s;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.fillStyle = '#0b1f17';
  for (let r = 0; r < n; r++) for (let k = 0; k < n; k++) if (qr.isDark(r, k)) ctx.fillRect((k + 4) * s, (r + 4) * s, s, s);
}

function copyText(b: HTMLElement, text: string) {
  void navigator.clipboard?.writeText(text).then(() => {
    b.textContent = 'Copied';
    b.classList.add('done');
    setTimeout(() => {
      b.textContent = 'Copy';
      b.classList.remove('done');
    }, 1600);
  });
}

async function markDone(steps: StepId[], quiet = false) {
  const before = new Set(draft.done);
  const added = steps.filter((s) => !before.has(s));
  if (!added.length) return;
  await save({ done: [...before, ...added] });
  if (!quiet && draft.path && added.some((s) => PATH_STEPS[draft.path!].includes(s))) toast(`Done: ${STEPS[added[added.length - 1]].title}`);
  openStep = null;
  renderPath();
}

function renderPath() {
  const path = draft.path;
  if (!path) return showQuestions();
  const prog = pathProgress(path, draft.done);
  $('path-eyebrow').textContent = `Path ${path}`;
  $('path-title').textContent = PATH_TITLES[path];
  $('path-line').textContent = pathLine(path);
  $('stepper-bar').replaceChildren(...prog.steps.map((s) => h('i', { class: prog.done.includes(s) ? 'done' : s === prog.current ? 'now' : '' })));
  $('stepper-label').textContent = prog.current ? `Step ${prog.index} of ${prog.total}` : `All ${prog.total} steps done`;
  const current = openStep && prog.steps.includes(openStep) ? openStep : prog.current;
  $('guide').replaceChildren(
    ...prog.steps.map((s, i) => {
      const def = STEPS[s];
      const done = prog.done.includes(s);
      const open = s === current;
      const li = h('li', { class: `gstep ${done ? 'done' : ''} ${s === prog.current ? 'now' : ''} ${open ? 'open' : ''}`, 'data-step': s });
      const head = h('button', { class: 'gstep-head', type: 'button', 'aria-expanded': String(open) }, h('span', { class: 'num' }, done ? '' : String(i + 1)), h('span', { class: 'gtitle' }, def.title), h('span', { class: 'gstate' }, done ? 'Done' : s === prog.current ? 'Now' : ''));
      head.addEventListener('click', () => {
        openStep = open ? null : s;
        renderPath();
      });
      li.append(head);
      if (open) {
        const body = h('div', { class: 'gstep-body' });
        if (def.time) body.append(h('p', { class: 'time' }, def.time));
        def.render(body);
        const foot = h('div', { class: 'gstep-foot' });
        if (done) {
          const undo = h('button', { class: 'btn ghost sm', type: 'button' }, 'Mark as not done');
          undo.addEventListener('click', async () => {
            await save({ done: draft.done.filter((x) => x !== s) });
            openStep = s;
            renderPath();
          });
          foot.append(h('span', { class: 'badge' }, 'Done'), undo);
        } else {
          const next = h('button', { class: 'btn block next', type: 'button' }, `${def.doneLabel} → next step`);
          next.addEventListener('click', () => void markDone([s], true));
          foot.append(next);
        }
        body.append(foot);
        li.append(body);
      }
      return li;
    }),
  );
  $('all-done').hidden = !!prog.current;
  if (draft.progress) $<HTMLAnchorElement>('all-done-go').href = draft.progress.customer_url;
  $('save-link').textContent = draft.id ? `${location.origin}/start/${draft.id}` : 'Saving…';
  watch(prog.current);
}

function pathLine(p: PathId): string {
  const who = draft.company_name ? `${draft.company_name}: you` : 'You';
  return {
    A: `${who} have an LEI and a company identity. About 10 minutes to go.`,
    B: `${who} have an LEI and the wallet. Next: a company identity from Global FinReg.`,
    C: `${who} have an LEI. Install the wallet, then get a company identity.`,
    D: 'You have the wallet. Get an LEI, then a company identity.',
    E: 'We take you through it all: the wallet, an LEI and a company identity.',
  }[p];
}

/** Auto-detection for the current step: the extension, the balance, PageID's own records. */
function watch(step: StepId | null) {
  for (const t of timers) clearInterval(t);
  timers = [];
  const every = (ms: number, f: () => unknown) => {
    void f();
    timers.push(window.setInterval(() => document.visibilityState === 'visible' && void f(), ms));
  };
  if (step === 'wallet' && !MOBILE) {
    every(3000, async () => {
      if (await detectWallet(400)) {
        walletFound = true;
        await save({ answers: { wallet: 'yes' } });
        toast('Concordium Wallet found');
        await markDone(['wallet'], true);
      }
    });
  }
  if (step === 'ccd' && draft.account) {
    every(10_000, async () => {
      try {
        const micro = await ccdBalance(draft.account!, netFetch);
        const was = balance?.micro;
        balance = { micro, at: Date.now() };
        if (enoughCcd(micro)) {
          toast(`${formatCcd(micro)} on the account: enough`);
          await markDone(['ccd'], true);
        } else if (was !== micro) updateBalance();
      } catch {
        /* next tick */
      }
    });
  }
  if (step === 'connect' || step === 'domains' || step === 'binding') {
    every(15_000, async () => {
      try {
        draft.progress = (await api<Draft>(`/api/start/${draft.id}`)).progress;
        await applyProgress();
      } catch {
        /* next tick */
      }
    });
  }
}

function updateBalance() {
  const box = document.querySelector<HTMLElement>('.ccd-box');
  if (!box) return;
  box.replaceChildren();
  renderCcd(box);
}

/** PageID's own records tick the last steps: verified → every step up to connect; domains; binding. */
async function applyProgress() {
  const p = draft.progress;
  if (!p || !draft.path) return;
  const steps = PATH_STEPS[draft.path];
  const add: StepId[] = [];
  if (p.verified) add.push(...steps.slice(0, steps.indexOf('connect') + 1));
  if (p.verified && p.domains > 0 && p.domains_verified === p.domains) add.push('domains');
  if (p.bound) add.push(...steps);
  if (add.length) await markDone([...new Set(add)]);
}

function showPath() {
  $('questions-view').hidden = true;
  $('path-view').hidden = false;
  document.body.dataset.view = 'path';
  if (location.hash !== '#steps') history.replaceState(null, '', `${location.pathname}#steps`);
  renderPath();
  void applyProgress();
}
function showQuestions() {
  for (const t of timers) clearInterval(t);
  timers = [];
  $('path-view').hidden = true;
  $('questions-view').hidden = false;
  document.body.dataset.view = 'questions';
  if (location.hash) history.replaceState(null, '', location.pathname);
  renderQuestions();
}

$('save-copy').addEventListener('click', () => copyText($('save-copy'), $('save-link').textContent ?? ''));

// ---- start -------------------------------------------------------------------------------------
async function init() {
  const params = new URLSearchParams(location.search);
  const fromUrl = location.pathname.match(/^\/start\/([A-Za-z0-9_-]{24})\/?$/)?.[1] ?? null;
  const detecting = detectWallet(1500).then((p) => {
    walletFound = !!p;
    return p;
  });
  if (fromUrl) {
    try {
      draft = await api<Draft>(`/api/start/${fromUrl}`);
      local.set(draft.id);
    } catch {
      local.set(null);
      history.replaceState(null, '', '/start/');
      $('expired').hidden = false;
    }
  } else {
    const saved = local.get();
    if (saved) {
      const d = await api<Draft>(`/api/start/${saved}`).catch(() => null);
      if (d?.path) {
        const prog = pathProgress(d.path, d.done);
        $('resume').hidden = false;
        $('resume-text').textContent = `You have saved progress: path ${d.path}, ${prog.current ? `step ${prog.index} of ${prog.total}` : 'all done'}.`;
        $<HTMLAnchorElement>('resume-go').href = `/start/${d.id}#steps`;
      } else if (!d) local.set(null);
    }
  }
  $('resume-new').addEventListener('click', () => {
    local.set(null);
    $('resume').hidden = true;
  });
  await detecting;
  // A wallet without a company identity, sent back from the connect step (/onboard/).
  if (params.get('switch') === 'no-company' && draft.id) {
    const answers = { ...draft.answers, wallet: 'yes' as Answer, companyId: 'no' as Answer };
    await save({ answers, path: choosePath(answers), done: draft.done.filter((s) => !['identity', 'approval', 'account', 'connect'].includes(s)) });
    history.replaceState(null, '', `/start/${draft.id}#steps`);
    toast('No company identity in that wallet yet: here is how to get one.');
  }
  // The extension is there: answer "Yes" for them (only in the page; saved with their first tap).
  if (walletFound && !draft.answers.wallet) {
    draft.answers.wallet = 'yes';
    if (draft.id) await save({ answers: { wallet: 'yes' }, path: choosePath(draft.answers) });
  }
  if (draft.id && draft.path) showPath();
  else showQuestions();
  document.body.dataset.ready = '1';
}
void init();

/** For the design screenshots and tests. */
(window as unknown as { pageidStart: unknown }).pageidStart = {
  get draft() {
    return draft;
  },
  get walletFound() {
    return walletFound;
  },
};
